import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Sparkles,
  Zap,
  Globe2,
  Swords,
  Bot,
  Compass,
  Rocket,
  Shield,
  Skull,
  CheckCircle2,
} from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { api } from "@/lib/api";

const featureKeys = [
  { icon: Users, t: "lp.f1" },
  { icon: Sparkles, t: "lp.f2" },
  { icon: Zap, t: "lp.f3" },
  { icon: Globe2, t: "lp.f4" },
  { icon: Swords, t: "lp.f5" },
  { icon: Bot, t: "lp.f6" },
  { icon: Skull, t: "lp.f7" },
];

export default function LandingPage() {
  const { t } = useT();
  const [liveStats, setLiveStats] = useState(null);

  useEffect(() => {
    api
      .get("/stats/public")
      .then((r) => setLiveStats(r.data))
      .catch(() => setLiveStats({ users_total: 0, countries_active: 0, games_tracked: 0 }));
  }, []);

  const stats = [
    {
      k: liveStats == null ? "—" : (liveStats.users_total ?? 0).toLocaleString(),
      v: t("lp.stats.pilots"),
    },
    {
      k: liveStats == null ? "—" : (liveStats.countries_active ?? 0).toLocaleString(),
      v: t("lp.stats.countries"),
    },
    {
      k: liveStats == null ? "—" : (liveStats.games_tracked ?? 0).toLocaleString(),
      v: t("lp.stats.games"),
    },
    { k: t("lp.stats.early_label") || "Early Access", v: t("lp.stats.uptime") },
  ];

  return (
    <div className="min-h-screen bg-radial-neon text-white" data-testid="landing-page">
      {/* NAV — mobile: logo + single primary CTA; desktop: + lang + sign in */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-3">
          <Logo to="/" />
          <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white" data-testid="lp-nav-features">{t("nav.features")}</a>
            <Link to="/pricing" className="hover:text-white" data-testid="lp-nav-pricing">{t("nav.pricing")}</Link>
            <Link to="/app/trends" className="hover:text-white" data-testid="lp-nav-trends">{t("nav.trends")}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            <Link to="/login" data-testid="lp-signin" className="hidden sm:inline-flex">
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-full"
              >
                {t("nav.signin")}
              </Button>
            </Link>
            <Link to="/login" data-testid="lp-get-started-top">
              <Button
                size="sm"
                className="bg-neon text-black hover:bg-neon-hover rounded-full font-bold shadow-neon px-4 sm:px-5"
              >
                {t("nav.signup")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-20 pb-14 sm:pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              {liveStats != null && liveStats.users_total < 100 ? (
                <FoundingPilotBadge t={t} count={liveStats.users_total} />
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neon/10 border border-neon/25 text-neon text-xs font-semibold uppercase tracking-wider">
                  <Compass size={14} /> {t("lp.kicker")}
                </span>
              )}
              <h1 className="mt-5 font-display font-black tracking-tight text-[34px] leading-[1.08] sm:text-5xl lg:text-6xl sm:leading-[1.05]">
                {t("lp.headline_1")}
                <br />
                <span className="text-neon text-neon-glow">{t("lp.headline_2")}</span>
              </h1>
              <p className="mt-5 text-zinc-300 text-base sm:text-lg max-w-xl">{t("lp.sub")}</p>

              {/* Mobile lang switcher — inline under sub so top bar stays clean */}
              <div className="mt-5 sm:hidden">
                <LanguageSwitcher />
              </div>

              <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/login" data-testid="hero-get-started" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto h-14 bg-neon text-black hover:bg-neon-hover rounded-full font-bold px-7 shadow-neon text-base"
                  >
                    {t("lp.cta_primary")}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/app/trends" data-testid="hero-explore-trends" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto h-14 rounded-full bg-transparent border-neon/60 text-neon hover:bg-neon/10 hover:text-neon px-7 text-base"
                  >
                    {t("lp.cta_secondary")}
                  </Button>
                </Link>
              </div>

              {/* Trust line */}
              <p className="mt-4 inline-flex items-center gap-2 text-xs sm:text-sm text-zinc-400" data-testid="hero-trust-line">
                <CheckCircle2 size={14} className="text-neon" />
                {t("lp.trust")}
              </p>

              <dl className="mt-8 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-xl">
                {stats.map((s) => (
                  <div
                    key={s.v}
                    className="rounded-2xl bg-zinc-900/60 border border-white/5 p-4"
                  >
                    <dt className="font-display text-xl sm:text-2xl font-black text-neon">{s.k}</dt>
                    <dd className="text-[11px] sm:text-xs text-zinc-400 mt-1">{s.v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="lg:col-span-5">
              <div className="relative animate-fade-up [animation-delay:120ms]">
                <div className="absolute -inset-6 bg-neon/20 blur-3xl rounded-full opacity-60 pointer-events-none" />
                <div className="relative rounded-3xl border border-white/10 bg-black/60 backdrop-blur-xl p-6 sm:p-8 grid place-items-center">
                  <img
                    src={BRAND.logoUrl}
                    alt="FicoPilot"
                    className="w-44 sm:w-72 drop-shadow-[0_0_40px_rgba(0,229,255,0.45)]"
                    data-testid="hero-logo"
                  />
                  <div className="mt-6 grid grid-cols-3 gap-2 w-full">
                    {["Valorant", "Call of Duty", "CS2"].map((g) => (
                      <div
                        key={g}
                        className="text-center text-xs font-semibold px-2 py-2 rounded-xl bg-white/5 border border-white/5 text-zinc-300"
                      >
                        {g}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-14 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-neon text-xs font-bold uppercase tracking-widest">{t("lp.features.kicker")}</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold">{t("lp.features.title")}</h2>
            <p className="mt-3 text-zinc-400">{t("lp.features.sub")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featureKeys.map((f, i) => {
              const Icon = f.icon;
              const isZombies = f.t === "lp.f7";
              return (
                <div
                  key={f.t}
                  className={`card-interactive rounded-2xl backdrop-blur-sm border p-6 animate-fade-up ${
                    isZombies
                      ? "bg-gradient-to-br from-lime-500/10 via-zinc-900/60 to-transparent border-lime-400/25"
                      : "bg-zinc-900/50 border-white/5"
                  }`}
                  style={{ animationDelay: `${i * 70}ms` }}
                  data-testid={`lp-feature-${i}`}
                >
                  <div className={`w-11 h-11 rounded-xl grid place-items-center ${
                    isZombies ? "bg-lime-400/15 border border-lime-400/30 text-lime-300" : "bg-neon/10 border border-neon/20 text-neon"
                  }`}>
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold">{t(`${f.t}.title`)}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{t(`${f.t}.desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PREVIEW CTA STRIP */}
      <section className="py-10 bg-zinc-950/70 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-neon/10 via-zinc-900/50 to-transparent border border-white/10 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 sm:gap-6 justify-between">
            <div>
              <p className="text-neon text-xs font-bold uppercase tracking-widest">{t("lp.preview.kicker")}</p>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold">
                {t("lp.preview.title")}
              </h3>
              <p className="mt-2 text-zinc-400 text-sm max-w-xl">
                {t("lp.preview.sub")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Link to="/app/trends" data-testid="preview-trends" className="flex-1 md:flex-initial">
                <Button className="w-full md:w-auto rounded-full bg-neon text-black hover:bg-neon-hover font-bold">
                  <Globe2 size={16} className="mr-2" /> {t("nav.trends")}
                </Button>
              </Link>
              <Link to="/app/teammates" data-testid="preview-teammates" className="flex-1 md:flex-initial">
                <Button variant="outline" className="w-full md:w-auto rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">
                  <Users size={16} className="mr-2" /> {t("nav.teammates")}
                </Button>
              </Link>
              <Link to="/app/play" data-testid="preview-play" className="flex-1 md:flex-initial">
                <Button variant="outline" className="w-full md:w-auto rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">
                  <Sparkles size={16} className="mr-2" /> {t("nav.play")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-14 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-neon text-xs font-bold uppercase tracking-widest">{t("lp.pricing.kicker")}</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold">{t("lp.pricing.title")}</h2>
            <p className="mt-3 text-zinc-400">{t("lp.pricing.sub")}</p>
            <p className="mt-3 inline-flex items-center gap-2 text-xs text-zinc-500">
              <CheckCircle2 size={12} className="text-neon" />
              {t("lp.trust")}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <PricingCard
              name={t("lp.pricing.free_name")}
              price="$0"
              period={t("lp.pricing.forever")}
              features={[
                t("lp.pricing.free.f1"),
                t("lp.pricing.free.f2"),
                t("lp.pricing.free.f3"),
                t("lp.pricing.free.f4"),
              ]}
              cta={t("lp.pricing.free_cta")}
              variant="ghost"
              to="/login"
              testid="pricing-free"
            />
            <PricingCard
              name={t("lp.pricing.premium_name")}
              price="$6.99"
              period={t("lp.pricing.per_month")}
              highlight
              popularLabel={t("lp.pricing.popular")}
              features={[
                t("lp.pricing.premium.f1"),
                t("lp.pricing.premium.f2"),
                t("lp.pricing.premium.f3"),
                t("lp.pricing.premium.f4"),
                t("lp.pricing.premium.f5"),
              ]}
              cta={t("lp.pricing.premium_cta")}
              variant="primary"
              to="/login"
              testid="pricing-premium"
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <div className="border-t border-white/5 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <Logo to="/" />
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <span className="inline-flex items-center gap-1">
              <Shield size={14} /> {t("lp.footer.built") || "Built for gamers worldwide"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Rocket size={14} /> {t("lp.footer.live") || "Live now"}
            </span>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

function PricingCard({ name, price, period, features, cta, variant, to, highlight, popularLabel, testid }) {
  return (
    <div
      className={`rounded-3xl p-6 sm:p-7 border ${
        highlight
          ? "border-neon/50 bg-gradient-to-b from-neon/10 to-transparent shadow-neon-lg"
          : "border-white/5 bg-zinc-900/40"
      }`}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-2xl font-bold">{name}</h3>
        {highlight && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-neon">
            {popularLabel}
          </span>
        )}
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-5xl font-black">{price}</span>
        <span className="text-sm text-zinc-400">{period}</span>
      </div>
      <ul className="mt-6 space-y-2 text-sm text-zinc-300">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="text-neon mt-0.5">✦</span>
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-6">
        <Link to={to}>
          <Button
            className={`w-full h-12 rounded-full font-bold ${
              variant === "primary"
                ? "bg-neon text-black hover:bg-neon-hover shadow-neon"
                : "bg-white/5 hover:bg-white/10 text-white"
            }`}
          >
            {cta}
          </Button>
        </Link>
      </div>
    </div>
  );
}

/**
 * Honest scarcity badge — only shown while users_total < 100. Turns the
 * low signup count from a weakness into a Founding-Pilot perk.
 */
function FoundingPilotBadge({ t, count }) {
  const remaining = Math.max(1, 100 - (count || 0));
  return (
    <span
      data-testid="founding-pilot-badge"
      className="relative inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-neon/15 via-neon/10 to-transparent border border-neon/40 text-neon text-xs font-bold uppercase tracking-wider shadow-neon"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-neon opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-neon" />
      </span>
      <Rocket size={14} />
      <span>{t("lp.founding_badge") || "Founding Pilot"}</span>
      <span className="text-neon/70 font-mono font-semibold normal-case tracking-normal">
        ·{" "}
        {(t("lp.founding_remaining") || "only {n} of 100 left").replace(
          "{n}",
          remaining,
        )}
      </span>
    </span>
  );
}
